import Typography from '@material-ui/core/Typography'
import { AddShoppingCartOutlined } from '@material-ui/icons'
import CheckIcon from '@material-ui/icons/Check'
import { orgSelectors } from 'ducks/orgs'
import { useTaxation } from 'hooks/useTaxation'
import { BaseTheme, Grid, styled } from 'opensolar-ui'
import { BundleRow, ProductBundleRow } from 'pages/ordering/deals/ProductBundleRow'
import { useSelector } from 'react-redux'
import { useBundleDeal } from '../deals/hooks/useBundleDeal'
import { Deal } from '../deals/types'

const GREEN = BaseTheme.palette.green[800]

const SectionCardTop = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: 25,
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  borderBottom: '1px solid ' + theme.palette.grey[100],
}))

const SectionCardBottom = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: 25,
  borderBottomLeftRadius: 8,
  borderBottomRightRadius: 8,
}))

const TotalsBox = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: 8,
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 6,
  width: '378px',
}))

const SavingsText = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.green[100],
  padding: '2px 8px',
  color: GREEN,
  fontWeight: 700,
  fontSize: 13,
  marginLeft: 6,
  borderRadius: theme.shape.borderRadius,
}))

const AddBundleButton = styled('button')<{ added?: boolean }>(({ added }) => ({
  padding: '10px 20px',
  backgroundColor: added ? GREEN : BaseTheme.palette.blue[800],
  color: BaseTheme.palette.white,
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: added ? 'default' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  whiteSpace: 'nowrap' as const,
  width: '100%',
  marginTop: 8,
  '&:hover': {
    opacity: added ? 1 : 0.9,
  },
}))

interface BundleDealSectionProps {
  deal: Deal
  currentProductCode?: string
}

const BundleDealSection = ({ deal, currentProductCode }: BundleDealSectionProps) => {
  const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)
  const distributorKey = deal.distributor

  const {
    isFetching,
    bundleCodes,
    sortedProducts,
    productsToAdd,
    selectedProducts,
    toggleProduct,
    allowSelection,
    otherLabel,
    bundleTotal,
    savings,
    discountedTotal,
    allInCart,
    addBundleToCart,
  } = useBundleDeal({ deal, currentProductCode })

  const countryCode = useSelector(orgSelectors.getOrgIso2)
  const { taxName } = useTaxation(countryCode)

  if (!bundleCodes.length) return null

  return (
    <>
      <SectionCardTop>
        <Typography variant="h6">Frequently Bought Together</Typography>
      </SectionCardTop>
      <SectionCardBottom>
        <BundleRow container wrap="nowrap">
          <ProductBundleRow
            products={sortedProducts}
            isFetching={isFetching}
            bundleCodes={bundleCodes}
            distributorKey={distributorKey}
            currentProductCode={currentProductCode}
            otherLabel={otherLabel}
            allowSelection={allowSelection}
            selectedProducts={selectedProducts}
            onToggleProduct={toggleProduct}
          />

          {bundleTotal > 0 && !isFetching && (
            <Grid container justify="flex-end" alignItems="flex-start" xs={12}>
              <TotalsBox>
                <Typography variant="caption" style={{ color: BaseTheme.palette.grey[700] }}>
                  Total bundle price ({productsToAdd.length} item{productsToAdd.length === 1 ? '' : 's'})
                </Typography>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography variant="h5" style={{ fontWeight: 700 }}>
                    {currencySymbol}
                    {discountedTotal.toFixed(2)}
                  </Typography>
                  {savings > 0 && (
                    <SavingsText>
                      Save {currencySymbol}
                      {savings.toFixed(2)}
                    </SavingsText>
                  )}
                </div>
                {taxName.length > 0 && (
                  <Typography variant="caption" style={{ color: BaseTheme.palette.grey[600] }}>
                    inc. {taxName}
                  </Typography>
                )}
                <AddBundleButton added={allInCart} onClick={addBundleToCart}>
                  {allInCart ? (
                    <>
                      <CheckIcon />
                      Added to cart
                    </>
                  ) : (
                    <>
                      <AddShoppingCartOutlined />
                      Add Bundle
                    </>
                  )}
                </AddBundleButton>
              </TotalsBox>
            </Grid>
          )}
        </BundleRow>
      </SectionCardBottom>
    </>
  )
}

export default BundleDealSection
